import { useLayoutEffect } from 'react'

import { IconCaretDown } from '@tabler/icons-react'
import { Menu } from 'antd'
import styled from 'styled-components'

import { BaseButton, BaseButtonProps } from './BaseButton'

import { ButtonProps } from '@/components/shared-components/BUI/Button'
import { Dropdown } from '@/components/shared-components/BUI/Dropdown'
import { PillProps, getColours } from '@/components/shared-components/BUI/Pill'
import { useLocale } from '@/context/localeContext'

import { buildPassTheme } from 'src/styles/theme'

const CLEAR_KEY = 'CLEAR'

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const Icon = styled.div`
  display: flex;
  align-items: center;

  & svg {
    margin-right: 1rem;
  }
`

export type KeyValue = {
  label: string
  icon?: React.ReactNode
  colour?: string
  variant?: PillProps['variant']
}

type Props = {
  options: Record<string, KeyValue>
  clear?: KeyValue
  value?: string
  onChange?: (value: string) => void
  noValueLabel?: string | React.ReactNode
  showCaret?: boolean
  size?: ButtonProps['size']
  selectFirstByDefault?: boolean
  buttonProps?: Omit<BaseButtonProps, 'variant' | 'size' | 'hasValue'>
  disabled?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const getIconColour = (option: KeyValue) => {
  if (!option) return null
  if (option.colour) return option.colour
  if (option.variant) return getColours(option.variant)?.iconColour
  return null
}

export const KeyValueDropdown: React.FC<Props> = ({
  options,
  clear,
  value,
  onChange,
  noValueLabel,
  showCaret,
  size,
  selectFirstByDefault,
  buttonProps,
  disabled,
  open,
  onOpenChange,
}) => {
  const { t } = useLocale()

  useLayoutEffect(() => {
    if (!value && selectFirstByDefault) {
      const firstKey = Object.keys(options)?.[0]
      onChange?.(firstKey)
    }
  }, [value, selectFirstByDefault, options, onChange])

  const selectedValue = value ? options[value] : clear ? clear : null

  const label: string | React.ReactNode =
    selectedValue?.label || noValueLabel || 'No value'

  return (
    <Dropdown
      overlay={
        <Menu
          style={{ maxHeight: '300px', overflow: 'auto' }}
          items={[
            ...(clear
              ? [
                  {
                    key: CLEAR_KEY,
                    label: (
                      <Row>
                        <Icon style={{ color: clear.colour }}>
                          {clear.icon}
                        </Icon>
                        <span>{clear.label}</span>
                      </Row>
                    ),
                    onClick: () => onChange(null),
                  },
                ]
              : []),
            ...Object.keys(options).map((option) => ({
              key: option,
              label: (
                <Row>
                  <Icon style={{ color: getIconColour(options[option]) }}>
                    {options[option].icon}
                  </Icon>
                  <span>{t(options[option].label)}</span>
                </Row>
              ),
              onClick: () => onChange(option),
            })),
          ]}
        />
      }
      placement="bottomLeft"
      trigger={['click']}
      disabled={disabled}
      open={open}
      onOpenChange={onOpenChange}
    >
      <BaseButton
        hasValue={!!value}
        icon={selectedValue?.icon}
        iconColour={getIconColour(selectedValue)}
        size={size}
        disabled={disabled}
        {...buttonProps}
      >
        {typeof label === 'string' ? t(label) : label}
        {showCaret && (
          <IconCaretDown
            color={buildPassTheme.colour.secondary.base}
            fill={buildPassTheme.colour.secondary.base}
            size={16}
          />
        )}
      </BaseButton>
    </Dropdown>
  )
}
